import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link as RouterLink } from 'react-router-dom';
import { 
  FaSearch, FaChevronDown, FaChevronUp, FaArrowUp, FaEnvelope, 
  FaInfoCircle, FaHandshake, FaCloud, FaUserCog, 
  FaCopyright, FaExclamationTriangle, FaBan, FaBalanceScale,
  FaExternalLinkAlt, FaCreditCard, FaWallet, FaLaptopCode
} from 'react-icons/fa';

// Section Data
const sections = [
  {
    id: "acceptance-of-terms",
    title: "1. Acceptance of Terms",
    icon: <FaHandshake />,
    intro: "Welcome to Mount Software Service. By using our services, you agree to comply with and be bound by these terms of service. By accessing or using our portal, you signify your acceptance of these terms and conditions.",
    subsections: [
      {
        title: "Acceptance of Terms",
        content: "By accessing or using Mount Software Service, you agree to comply with and be bound by the following terms of service. If you do not agree with any part of these terms, please do not use our portal."
      },
      {
        title: "Changes to Terms",
        content: "Mount Software Service reserves the right to modify, amend, or update these terms at any time. Such changes will be effective immediately upon posting. It is your responsibility to review this agreement periodically for any modifications. Continued use of the portal after changes are posted constitutes acceptance of those changes."
      },
      {
        title: "User Registration",
        content: "To access certain features of Mount Software Service, you may be required to register for an account. In doing so, you agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete."
      },
      {
        title: "User Conduct",
        content: "You agree to use Mount Software Service for lawful purposes and in a way that does not infringe on the rights of others, restrict or inhibit anyone else's use, or violate any applicable laws."
      },
      {
        title: "Privacy Policy",
        content: "Your use of Mount Software Service is also governed by our Privacy Policy. By using our portal, you consent to the terms of our Privacy Policy. You can read the full policy here: ",
        internalLink: {
          text: "Privacy Policy",
          path: "/PrivacyPolicy"
        }
      },
      {
        title: "Termination of Access",
        content: "Mount Software Service reserves the right, at its sole discretion, to terminate your account or restrict your access to the portal at any time, without notice, for any reason."
      },
      {
        title: "Disclaimer of Warranties",
        content: "Mount Software Service is provided 'as is' and 'as available' without any representations or warranties, express or implied."
      }
    ]
  },
  {
    id: "description-of-service",
    title: "2. Description of Service",
    icon: <FaCloud />,
    content: "Mount Software Service provides Cloud services. You are responsible for obtaining access to the services and that access may involve third-party fees (such as Internet service provider or airtime charges). Click below to view our Cloud products and services:",
    actionLink: {
      text: "Explore Cloud Products & Services",
      url: "https://www.mounts.in/products"
    }
  },
  {
    id: "pricing-and-payments",
    title: "3. Pricing and Payment Terms",
    icon: <FaCreditCard />,
    intro: "We offer SaaS subscription plans for our services. By choosing a paid service, you agree to the pricing and payment terms outlined below:",
    subsections: [
      {
        title: "Payment Authorization",
        content: "We process payments securely through our integrated payment gateways (such as Razorpay). By initiating a transaction, you authorize us and our payment processors to charge the designated billing method for the subscription fees."
      },
      {
        title: "Billing Cycle",
        content: "Subscription fees are billed in advance on a recurring basis (monthly, quarterly, or annually) depending on the plan you select. Subscriptions automatically renew at the end of each billing cycle unless cancelled."
      },
      {
        title: "Pricing Changes",
        content: "Mount Software Service reserves the right to modify subscription fees at any time. Any price changes will be communicated to you in advance and will take effect at the start of the next billing cycle."
      }
    ]
  },
  {
    id: "refund-and-cancellation",
    title: "4. Cancellation and Refund Policy",
    icon: <FaWallet />,
    intro: "We strive to provide excellent service. Please review our policy regarding cancellation and refunds:",
    subsections: [
      {
        title: "Non-Refundable Payments",
        content: "All payments made to Mount Software Service for SaaS products and subscriptions are strictly non-refundable. No refunds, credits, or prorated adjustments will be issued for partial periods of service, unused subscription time, or immediate cancellations."
      },
      {
        title: "Subscription Cancellation",
        content: "You can cancel your subscription at any time through your account settings or by contacting our support team at info@mounts.in. Upon cancellation, your service will remain active until the end of the current paid billing cycle, and no further automatic renewals will occur."
      },
      {
        title: "Billing Disputes",
        content: "If you believe there has been a billing error, please contact us within 15 days of the transaction date. We will investigate the issue and address it in accordance with applicable rules."
      }
    ]
  },
  {
    id: "service-delivery",
    title: "5. Service Delivery and Fulfillment",
    icon: <FaLaptopCode />,
    intro: "Since Mount Software Service provides digital SaaS applications, the delivery and fulfillment of services are conducted electronically:",
    subsections: [
      {
        title: "Immediate Service Activation",
        content: "Your SaaS account subscription is activated immediately upon successful payment confirmation from our payment gateway. You will receive an automated confirmation email with login and setup instructions."
      },
      {
        title: "No Physical Delivery",
        content: "As our products are digital cloud-based software, no physical shipping, delivery, or tracking details are applicable or required."
      }
    ]
  },
  {
    id: "user-responsibilities",
    title: "6. User Responsibilities",
    icon: <FaUserCog />,
    content: "You are responsible for maintaining the confidentiality of your account and password. You agree to use the services only for lawful purposes, and you accept responsibility for all activities that occur under your account or credentials."
  },
  {
    id: "intellectual-property",
    title: "7. Intellectual Property",
    icon: <FaCopyright />,
    content: "All content included in our services, including text, graphics, logos, and images, is the property of Mount Software Service or its licensors and is protected by copyright and other intellectual property laws."
  },
  {
    id: "limitation-of-liability",
    title: "8. Limitations of Liability",
    icon: <FaExclamationTriangle />,
    content: "Mount Software Service shall not be liable for any direct, indirect, incidental, special, consequential, or exemplary damages resulting from the use or inability to use the services."
  },
  {
    id: "termination",
    title: "9. Termination",
    icon: <FaBan />,
    content: "Mount Software Service reserves the right to terminate or suspend your account and access to the services at any time, without prior notice, for conduct that we believe violates these terms or is harmful to other users or our business."
  },
  {
    id: "governing-law",
    title: "10. Governing Law",
    icon: <FaBalanceScale />,
    content: "These terms are governed by and construed in accordance with the laws of India. Any actions arising out of these terms shall be filed only in the competent courts located within India."
  },
  {
    id: "contact-information",
    title: "11. Contact Information",
    icon: <FaEnvelope />,
    content: "If you have any questions about these terms of service, please contact us using the details below. We will address your questions as quickly as possible."
  }
];

// Styled Components
const PageWrapper = styled.div`
  background: radial-gradient(circle at 50% 0%, #151a2e 0%, #0a0d16 100%);
  color: #e2e8f0;
  min-height: 100vh;
  font-family: 'Inter', sans-serif;
  overflow-x: hidden;
  padding-bottom: 80px;
`;

const HeroSection = styled.div`
  padding: 100px 20px 60px;
  text-align: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 600px;
    height: 300px;
    background: radial-gradient(circle, rgba(75, 89, 247, 0.15) 0%, rgba(0,0,0,0) 70%);
    z-index: 0;
    pointer-events: none;
  }
`;

const HeroTitle = styled.h1`
  font-family: 'Outfit', sans-serif;
  font-size: 3.5rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #ffffff 30%, #a5b4fc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;


const ControlsWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto 40px;
  padding: 0 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 450px;
  min-width: 280px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 45px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  color: #fff;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(8px);

  &:focus {
    outline: none;
    border-color: rgba(75, 89, 247, 0.8);
    background: rgba(255, 255, 255, 0.06);
    box-shadow: 0 0 15px rgba(75, 89, 247, 0.25);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const SearchIcon = styled(FaSearch)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 0.95rem;
`;

const ToggleButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #e2e8f0;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(8px);

  &:hover {
    background: rgba(75, 89, 247, 0.15);
    border-color: rgba(75, 89, 247, 0.4);
    color: #fff;
  }
`;

const MainGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px;
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 40px;
  align-items: start;

  @media (max-width: 991px) {
    grid-template-columns: 1fr;
    padding: 0 20px;
    gap: 20px;
  }
`;

const Sidebar = styled.aside`
  position: sticky;
  top: 100px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  padding-right: 10px;

  @media (max-width: 991px) {
    display: none;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }
`;

const TocHeading = styled.h4`
  font-family: 'Outfit', sans-serif;
  color: #94a3b8;
  text-transform: uppercase;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  margin-bottom: 15px;
`;

const TocList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TocItem = styled.li`
  a {
    display: flex;
    align-items: center;
    gap: 10px;
    color: ${props => props.active ? '#818cf8' : '#94a3b8'};
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: ${props => props.active ? '600' : '400'};
    padding: 8px 12px;
    border-radius: 8px;
    background: ${props => props.active ? 'rgba(75, 89, 247, 0.1)' : 'transparent'};
    border-left: 3px solid ${props => props.active ? '#4b59f7' : 'transparent'};
    transition: all 0.2s ease;

    &:hover {
      color: #fff;
      background: rgba(255, 255, 255, 0.02);
    }
  }
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const TermsCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 30px;
  backdrop-filter: blur(12px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(75, 89, 247, 0.3);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    background: rgba(255, 255, 255, 0.03);
  }

  @media (max-width: 576px) {
    padding: 20px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
`;

const CardTitle = styled.h2`
  font-family: 'Outfit', sans-serif;
  font-size: 1.4rem;
  font-weight: 600;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: #818cf8;
    font-size: 1.25rem;
  }

  @media (max-width: 576px) {
    font-size: 1.15rem;
  }
`;

const CardArrow = styled.div`
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.03);
  transition: all 0.3s ease;

  ${TermsCard}:hover & {
    background: rgba(75, 89, 247, 0.15);
    color: #fff;
  }
`;

const CardBody = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  display: ${props => props.isOpen ? 'block' : 'none'};
  animation: fadeIn 0.4s ease;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(5px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const TextParagraph = styled.p`
  line-height: 1.7;
  color: #94a3b8;
  font-size: 0.95rem;
  margin-bottom: 15px;
`;

const SubsectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 25px;
`;

const SubsectionCard = styled.div`
  background: rgba(255, 255, 255, 0.01);
  border: 1px solid rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  padding: 20px;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(75, 89, 247, 0.15);
    background: rgba(255, 255, 255, 0.015);
  }
`;

const SubsectionTitle = styled.h3`
  font-family: 'Outfit', sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  color: #f1f5f9;
  margin-bottom: 10px;
`;

const CustomLink = styled(RouterLink)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #818cf8;
  text-decoration: none;
  font-weight: 500;
  border-bottom: 1px dashed rgba(129, 140, 248, 0.4);
  padding-bottom: 2px;
  transition: all 0.2s ease;

  &:hover {
    color: #a5b4fc;
    border-bottom-color: #a5b4fc;
    transform: translateY(-1px);
  }
`;

const ExternalLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #4b59f7 0%, #0467fb 100%);
  color: #fff;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 10px;
  margin-top: 15px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(75, 89, 247, 0.25);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(75, 89, 247, 0.4);
  }

  svg {
    font-size: 0.8rem;
  }
`;

const MailLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #818cf8;
  text-decoration: none;
  font-weight: 500;
  border-bottom: 1px dashed rgba(129, 140, 248, 0.4);
  padding-bottom: 2px;
  transition: all 0.2s ease;

  &:hover {
    color: #a5b4fc;
    border-bottom-color: #a5b4fc;
    transform: translateY(-1px);
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const ContactDetailCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(75, 89, 247, 0.2);
    background: rgba(255, 255, 255, 0.04);
  }
`;

const ContactLabel = styled.div`
  font-size: 0.8rem;
  color: #818cf8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
  font-weight: 600;
`;

const ContactValue = styled.div`
  font-size: 0.95rem;
  color: #f1f5f9;
  line-height: 1.5;
`;


const FabButton = styled.button`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #4b59f7;
  border: none;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(75, 89, 247, 0.4);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 100;
  opacity: ${props => props.visible ? 1 : 0};
  transform: ${props => props.visible ? 'scale(1)' : 'scale(0.8)'};
  pointer-events: ${props => props.visible ? 'auto' : 'none'};

  &:hover {
    background: #6366f1;
    transform: ${props => props.visible ? 'scale(1.1) translateY(-3px)' : 'none'};
    box-shadow: 0 6px 25px rgba(99, 102, 241, 0.5);
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 40px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px dashed rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  color: #94a3b8;
  font-size: 1rem;
`;

const TermsOfService = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState(
    sections.reduce((acc, sec) => ({ ...acc, [sec.id]: true }), {})
  );
  const [activeSection, setActiveSection] = useState(sections[0].id);
  const [showFab, setShowFab] = useState(false);

  // Scroll Spy and FAB visibility
  useEffect(() => {
    const handleScroll = () => {
      // FAB Visibility
      if (window.scrollY > 400) {
        setShowFab(true);
      } else {
        setShowFab(false);
      }

      // Scroll Spy active toc item
      const scrollPosition = window.scrollY + 250;
      for (const section of sections) {
        const el = document.getElementById(section.id);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleSection = (id) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const expandAll = () => {
    setExpandedSections(sections.reduce((acc, sec) => ({ ...acc, [sec.id]: true }), {}));
  };

  const collapseAll = () => {
    setExpandedSections(sections.reduce((acc, sec) => ({ ...acc, [sec.id]: false }), {}));
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleTocClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const offset = 100;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = el.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(id);
      
      // Auto-expand section if collapsed
      setExpandedSections(prev => ({ ...prev, [id]: true }));
    }
  };

  // Smart Search logic
  const filteredSections = sections.filter(sec => {
    const query = searchQuery.toLowerCase();
    const matchesTitle = sec.title.toLowerCase().includes(query);
    const matchesContent = sec.content?.toLowerCase().includes(query);
    const matchesIntro = sec.intro?.toLowerCase().includes(query);
    
    const matchesSubsections = sec.subsections?.some(sub => 
      sub.title.toLowerCase().includes(query) || 
      sub.content.toLowerCase().includes(query)
    );

    return matchesTitle || matchesContent || matchesIntro || matchesSubsections;
  });

  return (
    <PageWrapper>
      <HeroSection>
        <HeroTitle>Terms of Service</HeroTitle>
      </HeroSection>

      <ControlsWrapper>
        <SearchContainer>
          <SearchIcon />
          <SearchInput 
            type="text" 
            placeholder="Search terms details..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchContainer>
        <ToggleButtons>
          <ActionButton onClick={expandAll}>Expand All</ActionButton>
          <ActionButton onClick={collapseAll}>Collapse All</ActionButton>
        </ToggleButtons>
      </ControlsWrapper>

      <MainGrid>
        <Sidebar>
          <TocHeading>Table of Contents</TocHeading>
          <TocList>
            {sections.map((sec) => (
              <TocItem key={sec.id} active={activeSection === sec.id}>
                <a 
                  href={`#${sec.id}`}
                  onClick={(e) => handleTocClick(e, sec.id)}
                >
                  {sec.icon}
                  {sec.title.substring(3)}
                </a>
              </TocItem>
            ))}
          </TocList>
        </Sidebar>

        <ContentArea>
          {filteredSections.length > 0 ? (
            filteredSections.map((sec) => (
              <TermsCard key={sec.id} id={sec.id}>
                <CardHeader onClick={() => toggleSection(sec.id)}>
                  <CardTitle>
                    {sec.icon}
                    {sec.title}
                  </CardTitle>
                  <CardArrow>
                    {expandedSections[sec.id] ? <FaChevronUp /> : <FaChevronDown />}
                  </CardArrow>
                </CardHeader>

                <CardBody isOpen={expandedSections[sec.id]}>
                  {sec.intro && <TextParagraph>{sec.intro}</TextParagraph>}
                  {sec.content && (
                    <TextParagraph>
                      {sec.content}
                    </TextParagraph>
                  )}

                  {sec.actionLink && (
                    <div>
                      <ExternalLink href={sec.actionLink.url} target="_blank" rel="noopener noreferrer">
                        {sec.actionLink.text} <FaExternalLinkAlt />
                      </ExternalLink>
                    </div>
                  )}

                  {sec.id === 'contact-information' && (
                    <ContactGrid>
                      <ContactDetailCard>
                        <ContactLabel>Company Name</ContactLabel>
                        <ContactValue>Mount Software Service</ContactValue>
                      </ContactDetailCard>
                      <ContactDetailCard>
                        <ContactLabel>GSTIN</ContactLabel>
                        <ContactValue>09ASOPC6084A1ZS</ContactValue>
                      </ContactDetailCard>
                      <ContactDetailCard>
                        <ContactLabel>Email Address</ContactLabel>
                        <ContactValue>
                          <MailLink href="mailto:info@mounts.in">
                            <FaEnvelope style={{fontSize: '0.8rem', color: 'inherit', marginTop: 0}} /> info@mounts.in
                          </MailLink>
                        </ContactValue>
                      </ContactDetailCard>
                      <ContactDetailCard style={{ gridColumn: '1 / -1' }}>
                        <ContactLabel>Registered Address</ContactLabel>
                        <ContactValue>S-3, 646, BR Apartment, Shalimar Garden Extn 1, Sahibabad, Ghaziabad, Uttar Pradesh, 201005</ContactValue>
                      </ContactDetailCard>
                    </ContactGrid>
                  )}

                  {sec.subsections && (
                    <SubsectionWrapper>
                      {sec.subsections.map((sub, idx) => (
                        <SubsectionCard key={idx}>
                          <SubsectionTitle>{sub.title}</SubsectionTitle>
                          <TextParagraph style={{margin: 0}}>
                            {sub.content}
                            {sub.internalLink && (
                              <CustomLink to={sub.internalLink.path}>
                                {sub.internalLink.text}
                              </CustomLink>
                            )}
                          </TextParagraph>
                        </SubsectionCard>
                      ))}
                    </SubsectionWrapper>
                  )}
                </CardBody>
              </TermsCard>
            ))
          ) : (
            <NoResults>
              <FaInfoCircle style={{fontSize: '2rem', marginBottom: '10px', color: '#818cf8'}} />
              <p>No matching details found for "{searchQuery}"</p>
            </NoResults>
          )}
        </ContentArea>
      </MainGrid>

      <FabButton visible={showFab} onClick={scrollToTop} aria-label="Scroll to top">
        <FaArrowUp />
      </FabButton>
    </PageWrapper>
  );
};

export default TermsOfService;
